import { apiRequest, fetchAuthenticatedFileBlob } from './api';

export async function fetchAdminDashboard() {
  const response = await apiRequest('/admin/dashboard', { method: 'GET', withAuth: true });
  return response.data;
}

export async function fetchAdminPaiementsImpayes() {
  const response = await apiRequest('/admin/paiements/impayes', { method: 'GET', withAuth: true });
  return response?.data ?? [];
}

export async function fetchAdminPaiementsProjections() {
  const response = await apiRequest('/admin/paiements/projections', { method: 'GET', withAuth: true });
  return response?.data;
}

export async function validateAdminVirement(transactionId) {
  const response = await apiRequest(`/admin/transactions/${encodeURIComponent(transactionId)}/valider-virement`, {
    method: 'POST',
    withAuth: true,
  });
  return response?.data;
}

export async function runAdminPaymentDueAlerts() {
  const response = await apiRequest('/admin/paiements/relancer-alertes', {
    method: 'POST',
    withAuth: true,
  });
  return response?.data;
}

export async function fetchAdminCommunicationModule11() {
  const response = await apiRequest('/admin/communication/module11', { method: 'GET', withAuth: true });
  return response?.data;
}

export async function fetchAdminSecurityModule12(params = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const query = new URLSearchParams();
  query.set('page', String(page));
  query.set('limit', String(limit));
  const response = await apiRequest(`/admin/security/module12?${query.toString()}`, {
    method: 'GET',
    withAuth: true,
  });
  return response?.data;
}

export async function runAdminDatabaseBackup() {
  const response = await apiRequest('/admin/security/backup/run', {
    method: 'POST',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Échec de la sauvegarde');
  }
  return response;
}

export async function downloadAdminDatabaseBackup(fileName) {
  const { downloadAuthenticatedFile } = await import('./api');
  await downloadAuthenticatedFile(
    `/admin/security/backup/download/${encodeURIComponent(fileName)}`,
    fileName,
  );
}

export async function restoreAdminDatabaseBackup({ fileName, confirmation }) {
  const response = await apiRequest('/admin/security/backup/restore', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ fileName, confirmation }),
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Échec de la restauration');
  }
  return response;
}

export async function restoreAdminDatabaseBackupFromUpload(file, confirmation) {
  const form = new FormData();
  form.append('file', file);
  form.append('confirmation', confirmation);
  const response = await apiRequest('/admin/security/backup/restore/upload', {
    method: 'POST',
    withAuth: true,
    body: form,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Échec de la restauration');
  }
  return response;
}

export async function fetchAdminPurgeCatalog() {
  const response = await apiRequest('/admin/security/purge/catalog', {
    method: 'GET',
    withAuth: true,
  });
  return response?.data ?? [];
}

export async function purgeAdminData({ category, password, confirmationPhrase }) {
  const response = await apiRequest('/admin/security/purge', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ category, password, confirmationPhrase }),
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Échec de la purge');
  }
  return response;
}

export async function fetchAdminCorbeille(params = {}) {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.typeEntite) query.set('typeEntite', String(params.typeEntite));
  if (params.search) query.set('search', String(params.search));
  const qs = query.toString() ? `?${query}` : '';
  const response = await apiRequest(`/admin/corbeille${qs}`, {
    method: 'GET',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Impossible de charger la corbeille');
  }
  return response?.data;
}

export async function removeAdminCorbeilleItem(id) {
  const response = await apiRequest(`/admin/corbeille/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Suppression impossible');
  }
  return response;
}

export async function restoreAdminCorbeilleItem(id) {
  const response = await apiRequest(`/admin/corbeille/${encodeURIComponent(id)}/restaurer`, {
    method: 'POST',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Restauration impossible');
  }
  return response;
}

export async function emptyAdminCorbeille(confirmationPhrase) {
  const response = await apiRequest('/admin/corbeille/vider', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ confirmationPhrase }),
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Impossible de vider la corbeille');
  }
  return response;
}

export async function fetchAdminAuditActionDetail(auditId) {
  const response = await apiRequest(`/admin/security/audit/${encodeURIComponent(auditId)}`, {
    method: 'GET',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Action introuvable');
  }
  return response?.data;
}

export async function fetchAdminApprenantActivities(params = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const query = new URLSearchParams();
  query.set('page', String(page));
  query.set('limit', String(limit));
  if (params.categorie) query.set('categorie', String(params.categorie));
  if (params.idApprenant) query.set('idApprenant', String(params.idApprenant));
  if (params.q) query.set('q', String(params.q));
  const response = await apiRequest(`/admin/activites-apprenants?${query.toString()}`, {
    method: 'GET',
    withAuth: true,
  });
  return response?.data;
}

export async function fetchAdminApprenantActivityDetail(activityId) {
  const response = await apiRequest(`/admin/activites-apprenants/${encodeURIComponent(activityId)}`, {
    method: 'GET',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Activité introuvable');
  }
  return response?.data;
}

export async function fetchAdminFormateurActivities(params = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const query = new URLSearchParams();
  query.set('page', String(page));
  query.set('limit', String(limit));
  if (params.categorie) query.set('categorie', String(params.categorie));
  if (params.idFormateur) query.set('idFormateur', String(params.idFormateur));
  if (params.q) query.set('q', String(params.q));
  const response = await apiRequest(`/admin/activites-formateurs?${query.toString()}`, {
    method: 'GET',
    withAuth: true,
  });
  return response?.data;
}

export async function fetchAdminFormateurActivityDetail(activityId) {
  const response = await apiRequest(`/admin/activites-formateurs/${encodeURIComponent(activityId)}`, {
    method: 'GET',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Activité introuvable');
  }
  return response?.data;
}

export async function fetchAdminAmbassadeurActivities(params = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 10;
  const query = new URLSearchParams();
  query.set('page', String(page));
  query.set('limit', String(limit));
  if (params.categorie) query.set('categorie', String(params.categorie));
  if (params.idAmbassadeur) query.set('idAmbassadeur', String(params.idAmbassadeur));
  if (params.q) query.set('q', String(params.q));
  const response = await apiRequest(`/admin/activites-ambassadeurs?${query.toString()}`, {
    method: 'GET',
    withAuth: true,
  });
  return response?.data;
}

export async function fetchAdminAmbassadeurActivityDetail(activityId) {
  const response = await apiRequest(`/admin/activites-ambassadeurs/${encodeURIComponent(activityId)}`, {
    method: 'GET',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Activité introuvable');
  }
  return response?.data;
}

export async function broadcastAdminCommunicationAlert(payload) {
  const response = await apiRequest('/admin/communication/broadcast', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Échec de la diffusion');
  }
  return { data: response?.data, message: response?.message };
}

export async function runAdminPaymentDueAlertForInscription(inscriptionId) {
  const response = await apiRequest(`/admin/paiements/relancer-alertes/${encodeURIComponent(inscriptionId)}`, {
    method: 'POST',
    withAuth: true,
  });
  if (response?.success === false) {
    throw new Error(response?.message || 'Échec de la relance');
  }
  return { data: response?.data, message: response?.message };
}

export async function fetchAdminInscriptions() {
  const response = await apiRequest('/admin/inscriptions', { method: 'GET', withAuth: true });
  const data = response.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return {
      enAttente: data.enAttente ?? [],
      actives: data.actives ?? [],
    };
  }
  return { enAttente: [], actives: [] };
}

export async function fetchTarifs(idFiliere) {
  const qs = idFiliere ? `?idFiliere=${encodeURIComponent(idFiliere)}` : '';
  const response = await apiRequest(`/tarifs${qs}`, { method: 'GET', withAuth: false });
  return response.data ?? [];
}

export async function fetchTarifsFilieresOverrides() {
  const response = await apiRequest('/tarifs/filieres', { method: 'GET', withAuth: false });
  return response.data ?? [];
}

export async function fetchAdminFiliereTarifs(idFiliere) {
  const response = await apiRequest(`/admin/filieres/${encodeURIComponent(idFiliere)}/tarifs`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function putAdminFiliereTarifs(idFiliere, tarifs) {
  const response = await apiRequest(`/admin/filieres/${encodeURIComponent(idFiliere)}/tarifs`, {
    method: 'PUT',
    withAuth: true,
    body: JSON.stringify({ tarifs }),
  });
  return response.data;
}

export async function fetchAdminTarifFormateur() {
  const response = await apiRequest('/admin/tarifs/formateur', { method: 'GET', withAuth: true });
  return response.data ?? null;
}

export async function patchAdminTarifFormation(id, payload) {
  const response = await apiRequest(`/admin/tarifs/formation/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminTarifFormateur(id, payload) {
  const response = await apiRequest(`/admin/tarifs/formateur/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminParrainageSettings() {
  const response = await apiRequest('/admin/parrainage/settings', { method: 'GET', withAuth: true });
  return response.data ?? { bonusInscriptionCdf: 0, regles: [] };
}

export async function patchAdminParrainageSettings(payload) {
  const response = await apiRequest('/admin/parrainage/settings', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminParrainageRegle(id, payload) {
  const response = await apiRequest(`/admin/parrainage/regles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchFilieres() {
  const response = await apiRequest('/filieres', { method: 'GET', withAuth: false });
  return response.data ?? [];
}

export async function fetchCohortes(idFiliere) {
  const query = idFiliere ? `?idFiliere=${encodeURIComponent(idFiliere)}` : '';
  const response = await apiRequest(`/cohortes${query}`, { method: 'GET', withAuth: false });
  return response.data ?? [];
}

export async function fetchAdminCohortes(idFiliere) {
  const query = idFiliere ? `?idFiliere=${encodeURIComponent(idFiliere)}` : '';
  const response = await apiRequest(`/admin/cohortes${query}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminCohorte(payload) {
  const response = await apiRequest('/admin/cohortes', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminCohorte(id, payload) {
  const response = await apiRequest(`/admin/cohortes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminSalles() {
  const response = await apiRequest('/admin/salles', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function patchAdminSalle(id, payload) {
  const response = await apiRequest(`/admin/salles/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminVacations() {
  const response = await apiRequest('/admin/vacations', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchAdminPlanningGrid() {
  const response = await apiRequest('/admin/planning/grille', { method: 'GET', withAuth: true });
  return response.data;
}

export async function fetchAdminChargeHoraireGrille(jourSemaine) {
  const qs = jourSemaine ? `?jourSemaine=${encodeURIComponent(jourSemaine)}` : '';
  const response = await apiRequest(`/admin/charge-horaire/grille${qs}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function putAdminChargeHoraireAffectation(payload) {
  const response = await apiRequest('/admin/charge-horaire/affectations', {
    method: 'PUT',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminSalleAvailability() {
  const response = await apiRequest('/admin/planning/disponibilite-salles', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchAdminFormateurScheduleConflicts() {
  const response = await apiRequest('/admin/planning/conflits-formateurs', {
    method: 'GET',
    withAuth: true,
  });
  return (
    response.data ?? {
      totalConflicts: 0,
      formateursEnConflit: 0,
      conflicts: [],
    }
  );
}

export async function fetchDomaines() {
  const response = await apiRequest('/domaines', { method: 'GET', withAuth: false });
  return response.data ?? [];
}

export async function fetchAdminDomaines() {
  const response = await apiRequest('/admin/domaines', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminDomaine(payload) {
  const response = await apiRequest('/admin/domaines', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminDomaine(id, payload) {
  const response = await apiRequest(`/admin/domaines/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminFilieres() {
  const response = await apiRequest('/admin/filieres', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminFiliere(payload) {
  const response = await apiRequest('/admin/filieres', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminFiliere(id, payload) {
  const response = await apiRequest(`/admin/filieres/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchFiliereCapaciteSuggestion(idFiliere) {
  const response = await apiRequest(`/admin/filieres/${encodeURIComponent(idFiliere)}/capacite-suggestion`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function createAdminSalle(payload) {
  const response = await apiRequest('/admin/salles', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminDocuments(statut) {
  const query = statut ? `?statut=${encodeURIComponent(statut)}` : '';
  const response = await apiRequest(`/admin/documents${query}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function validateAdminDocument(id, payload) {
  const response = await apiRequest(`/admin/documents/${encodeURIComponent(id)}/validation`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchPlanningSession(sessionId, payload) {
  const response = await apiRequest(`/admin/planning/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function createPlanningSession(payload) {
  const response = await apiRequest('/admin/planning/sessions', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function deletePlanningSession(sessionId) {
  const response = await apiRequest(`/admin/planning/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    withAuth: true,
  });
  return response.data;
}

export async function regeneratePlanningGrid() {
  const response = await apiRequest('/admin/planning/regenerer-grille', {
    method: 'POST',
    withAuth: true,
  });
  return response.data;
}

export async function fetchBusinessSettings() {
  const response = await apiRequest('/settings/business', { method: 'GET', withAuth: false });
  return response.data;
}

export async function patchCurrencySettings(payload) {
  const response = await apiRequest('/admin/settings/currency', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminApprenants() {
  const response = await apiRequest('/admin/apprenants', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchAdminApprenantDetail(apprenantId) {
  const response = await apiRequest(`/admin/apprenants/${encodeURIComponent(apprenantId)}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function fetchAcademicVacations() {
  const response = await apiRequest('/admin/academique/vacations', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchAcademicSessions(idCohorte) {
  const response = await apiRequest(
    `/admin/academique/cohortes/${encodeURIComponent(idCohorte)}/sessions`,
    { method: 'GET', withAuth: true },
  );
  return response.data ?? [];
}

export async function fetchAttendanceSheet(idSession, dateSeance) {
  const q = dateSeance ? `?dateSeance=${encodeURIComponent(dateSeance)}` : '';
  const response = await apiRequest(
    `/admin/academique/sessions/${encodeURIComponent(idSession)}/presences${q}`,
    { method: 'GET', withAuth: true },
  );
  return response.data;
}

export async function saveAttendanceSheet(idSession, payload) {
  const response = await apiRequest(
    `/admin/academique/sessions/${encodeURIComponent(idSession)}/presences`,
    { method: 'POST', withAuth: true, body: JSON.stringify(payload) },
  );
  return response.data;
}

export async function fetchAttendanceSheetRegistry(idSession) {
  const response = await apiRequest(
    `/admin/academique/sessions/${encodeURIComponent(idSession)}/presences/registre`,
    { method: 'GET', withAuth: true },
  );
  return response.data;
}

export function attendanceSheetExportPdfPath(idSession, dateSeance) {
  const q = `?dateSeance=${encodeURIComponent(dateSeance)}`;
  return `/admin/academique/sessions/${encodeURIComponent(idSession)}/presences/export.pdf${q}`;
}

export async function fetchCohorteNotes(idCohorte) {
  const response = await apiRequest(
    `/admin/academique/cohortes/${encodeURIComponent(idCohorte)}/notes`,
    { method: 'GET', withAuth: true },
  );
  return response.data ?? [];
}

export async function saveAcademicNote(payload) {
  const response = await apiRequest('/admin/academique/notes', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function deleteAcademicNote(id) {
  await apiRequest(`/admin/academique/notes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    withAuth: true,
  });
}

export async function runCohorteDeliberation(idCohorte, payload) {
  const response = await apiRequest(
    `/admin/academique/cohortes/${encodeURIComponent(idCohorte)}/deliberation`,
    { method: 'POST', withAuth: true, body: JSON.stringify(payload) },
  );
  return response.data;
}

export async function fetchCohorteClassement(idCohorte, periode) {
  const q = periode ? `?periode=${encodeURIComponent(periode)}` : '';
  const response = await apiRequest(
    `/admin/academique/cohortes/${encodeURIComponent(idCohorte)}/classement${q}`,
    { method: 'GET', withAuth: true },
  );
  return response.data;
}

export async function previewCohorteClassement(idCohorte, periode, seuilAdmissionSur20) {
  const params = new URLSearchParams({ periode });
  if (seuilAdmissionSur20 != null) {
    params.set('seuilAdmissionSur20', String(seuilAdmissionSur20));
  }
  const response = await apiRequest(
    `/admin/academique/cohortes/${encodeURIComponent(idCohorte)}/classement/preview?${params}`,
    { method: 'GET', withAuth: true },
  );
  return response.data;
}

export async function patchNoteStatutDepot(noteId, statutDepot) {
  const response = await apiRequest(
    `/admin/academique/notes/${encodeURIComponent(noteId)}/statut-depot`,
    { method: 'PATCH', withAuth: true, body: JSON.stringify({ statutDepot }) },
  );
  return response.data;
}

export async function fetchSoutenancePlanning(idCohorte) {
  const response = await apiRequest(
    `/admin/academique/cohortes/${encodeURIComponent(idCohorte)}/soutenances`,
    { method: 'GET', withAuth: true },
  );
  return response.data ?? [];
}

export async function planifierSoutenanceAdmin(idCohorte, payload) {
  const response = await apiRequest(
    `/admin/academique/cohortes/${encodeURIComponent(idCohorte)}/soutenances`,
    { method: 'POST', withAuth: true, body: JSON.stringify(payload) },
  );
  return response.data;
}

export async function fetchEvaluationsFormateurAdmin(idCohorte) {
  const q = idCohorte ? `?idCohorte=${encodeURIComponent(idCohorte)}` : '';
  const response = await apiRequest(`/admin/academique/evaluations-formateur${q}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data ?? [];
}

export async function fetchCohorteDeliberations(idCohorte) {
  const response = await apiRequest(
    `/admin/academique/cohortes/${encodeURIComponent(idCohorte)}/deliberations`,
    { method: 'GET', withAuth: true },
  );
  return response.data ?? [];
}

export async function downloadDeliberationPdf(deliberationId) {
  const blob = await fetchAuthenticatedFileBlob(
    `/admin/academique/deliberations/${encodeURIComponent(deliberationId)}/pdf`,
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pv-deliberation-${deliberationId}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchAcademicConfiguration() {
  const response = await apiRequest('/admin/academique/configuration', { method: 'GET', withAuth: true });
  return response.data;
}

export async function patchAcademicConfiguration(payload) {
  const response = await apiRequest('/admin/academique/configuration', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminFormateurs() {
  const response = await apiRequest('/admin/formateurs', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchFormateurDetail(id) {
  const response = await apiRequest(`/admin/formateurs/${encodeURIComponent(id)}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function fetchAdminFormateurTarifs(idFormateur) {
  const response = await apiRequest(`/admin/formateurs/${encodeURIComponent(idFormateur)}/tarifs`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function putAdminFormateurTarifs(idFormateur, payload) {
  const response = await apiRequest(`/admin/formateurs/${encodeURIComponent(idFormateur)}/tarifs`, {
    method: 'PUT',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response;
}

export async function createAdminFormateur(payload) {
  const response = await apiRequest('/admin/formateurs', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return { data: response.data, message: response.message };
}

export async function resendFormateurAccess(id) {
  const response = await apiRequest(`/admin/formateurs/${encodeURIComponent(id)}/renvoyer-acces`, {
    method: 'POST',
    withAuth: true,
  });
  return response;
}

export async function patchAdminFormateur(id, payload) {
  const response = await apiRequest(`/admin/formateurs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminAmbassadeurs() {
  const response = await apiRequest('/admin/ambassadeurs', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminAmbassadeur(payload) {
  const response = await apiRequest('/admin/ambassadeurs', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return { data: response.data, message: response.message };
}

export async function resendAmbassadeurAccess(id) {
  const response = await apiRequest(`/admin/ambassadeurs/${encodeURIComponent(id)}/renvoyer-acces`, {
    method: 'POST',
    withAuth: true,
  });
  return response;
}

export async function patchAdminAmbassadeur(id, payload) {
  const response = await apiRequest(`/admin/ambassadeurs/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function uploadAdminFormateurFile(file) {
  const form = new FormData();
  form.append('file', file);
  const response = await apiRequest('/auth/upload-registration-document', {
    method: 'POST',
    withAuth: true,
    body: form,
  });
  const path = response?.data?.path || response?.data?.url;
  if (typeof path === 'string' && path.startsWith('/uploads/')) return path;
  if (typeof response?.data?.url === 'string') {
    try {
      return new URL(response.data.url).pathname;
    } catch {
      return response.data.url;
    }
  }
  throw new Error('Réponse upload invalide');
}

/** Upload document privé (image/PDF) — retourne chemin + jeton pour rattachement. */
export async function uploadAdminRegistrationDocument(file) {
  const form = new FormData();
  form.append('file', file);
  const response = await apiRequest('/auth/upload-registration-document', {
    method: 'POST',
    withAuth: true,
    body: form,
  });
  const path = response?.data?.path || response?.data?.url;
  const uploadToken = response?.data?.uploadToken ?? null;
  let resolvedPath = path;
  if (typeof path !== 'string' || !path.startsWith('/uploads/')) {
    if (typeof response?.data?.url === 'string') {
      try {
        resolvedPath = new URL(response.data.url).pathname;
      } catch {
        resolvedPath = response.data.url;
      }
    }
  }
  if (typeof resolvedPath !== 'string' || !resolvedPath.startsWith('/uploads/')) {
    throw new Error('Réponse upload invalide');
  }
  return { path: resolvedPath, uploadToken };
}

export async function fetchAdminFormateurDocuments(idFormateur) {
  const response = await apiRequest(`/admin/formateurs/${encodeURIComponent(idFormateur)}/documents`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data ?? [];
}

export async function validateAdminFormateurDocument(documentId, payload) {
  const response = await apiRequest(`/admin/formateurs/documents/${encodeURIComponent(documentId)}/validation`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function validateAdminFormateurReferencement(idFormateur, refId, payload) {
  const response = await apiRequest(
    `/admin/formateurs/${encodeURIComponent(idFormateur)}/referencements/${encodeURIComponent(refId)}/validation`,
    { method: 'PATCH', withAuth: true, body: JSON.stringify(payload) },
  );
  return response.data;
}

export async function syncPrestationsFormateur(payload = {}) {
  const response = await apiRequest('/admin/prestations-formateur/sync', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

/** Alias explicite pour la sync depuis les sessions terminées. */
export const syncPrestationsFromSessions = syncPrestationsFormateur;

export async function fetchPrestationsFormateur(idFormateur) {
  const q = idFormateur ? `?idFormateur=${encodeURIComponent(idFormateur)}` : '';
  const response = await apiRequest(`/admin/prestations-formateur${q}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data ?? [];
}

export async function markPrestationsPayees(ids, modePaiement = 'ESPECES') {
  const response = await apiRequest('/admin/prestations-formateur/payer', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ ids, modePaiement }),
  });
  return response.data;
}

export async function markPrestationsRealisees(ids) {
  const response = await apiRequest('/admin/prestations-formateur/realiser', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ ids }),
  });
  return response.data;
}

export async function payPrestationsViaWallet(ids) {
  const response = await apiRequest('/admin/prestations-formateur/payer-wallet', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ ids }),
  });
  return response.data;
}

export async function markPrestationComponentsPayees(ids, composante, modePaiement = 'ESPECES') {
  const response = await apiRequest('/admin/prestations-formateur/payer-composante', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ ids, composante, modePaiement }),
  });
  return response.data;
}

export async function payPrestationComponentsViaWallet(ids, composante) {
  const response = await apiRequest('/admin/prestations-formateur/payer-wallet-composante', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ ids, composante }),
  });
  return response.data;
}

export async function downloadFormateurRemunerationPdf(idFormateur, depuis, jusquA) {
  const params = new URLSearchParams();
  if (depuis) params.set('depuis', depuis);
  if (jusquA) params.set('jusquA', jusquA);
  const q = params.toString() ? `?${params}` : '';
  const blob = await fetchAuthenticatedFileBlob(
    `/admin/formateurs/${encodeURIComponent(idFormateur)}/remuneration/pdf${q}`,
  );
  const url = URL.createObjectURL(blob.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `etat-remuneration-${idFormateur}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function createAcademicVacation(payload) {
  const response = await apiRequest('/admin/academique/vacations', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAcademicVacation(id, payload) {
  const response = await apiRequest(`/admin/academique/vacations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function deleteAcademicVacation(id) {
  await apiRequest(`/admin/academique/vacations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    withAuth: true,
  });
}

export async function fetchPublicExcellence(periode) {
  const q = periode ? `?periode=${encodeURIComponent(periode)}` : '';
  const response = await apiRequest(`/excellence${q}`, { method: 'GET', withAuth: false });
  return response.data;
}

export async function fetchApprenantExcellence(periode) {
  const q = periode ? `?periode=${encodeURIComponent(periode)}` : '';
  const response = await apiRequest(`/profil/excellence${q}`, { method: 'GET', withAuth: true });
  return response.data;
}

export async function respondExcellenceOrientation(orientationId, accept) {
  const response = await apiRequest(`/profil/excellence/orientations/${orientationId}/reponse`, {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ accept }),
  });
  return response;
}

export async function fetchExcellenceShortlist(periode) {
  const q = periode ? `?periode=${encodeURIComponent(periode)}` : '';
  const response = await apiRequest(`/admin/excellence/shortlist${q}`, { method: 'GET', withAuth: true });
  return response.data;
}

export async function fetchExcellenceAdminOverview(periode) {
  const q = periode ? `?periode=${encodeURIComponent(periode)}` : '';
  const response = await apiRequest(`/admin/excellence/overview${q}`, { method: 'GET', withAuth: true });
  return response.data;
}

export async function fetchExcellenceOpportunites(periode) {
  const q = periode ? `?periode=${encodeURIComponent(periode)}` : '';
  const response = await apiRequest(`/admin/excellence/opportunites${q}`, { method: 'GET', withAuth: true });
  return response.data;
}

export async function fetchExcellencePartenaires(options = {}) {
  const qs = new URLSearchParams();
  if (options.includeInactive) qs.set('tous', '1');
  const q = qs.toString() ? `?${qs}` : '';
  const response = await apiRequest(`/admin/excellence/partenaires${q}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

/** Détache un partenaire Partenariat des opportunités excellence (désactive le lien). */
export async function setExcellencePartenaireLien(id, payload = {}) {
  const partnerId = String(id ?? '').trim();
  if (!partnerId) {
    throw new Error('Identifiant partenaire requis');
  }
  const response = await apiRequest(
    `/admin/excellence/partenaires/${encodeURIComponent(partnerId)}/lien`,
    {
      method: 'PATCH',
      withAuth: true,
      body: JSON.stringify({
        lienActif: payload.lienActif != null ? Boolean(payload.lienActif) : false,
        idOpportunite: payload.idOpportunite ?? null,
      }),
    },
  );
  if (response?.success === false) {
    throw new Error(response?.message || 'Mise à jour du lien partenaire impossible');
  }
  return response;
}

export async function fetchExcellencePeriodesAdmin() {
  const response = await apiRequest('/admin/excellence/periodes', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function saveExcellencePeriode(payload, code) {
  const response = await apiRequest(
    code
      ? `/admin/excellence/periodes/${encodeURIComponent(code)}`
      : '/admin/excellence/periodes',
    {
      method: code ? 'PATCH' : 'POST',
      withAuth: true,
      body: JSON.stringify(payload),
    },
  );
  return response;
}

export async function deleteExcellencePeriode(code) {
  const response = await apiRequest(`/admin/excellence/periodes/${encodeURIComponent(code)}`, {
    method: 'DELETE',
    withAuth: true,
  });
  return response;
}

export async function fetchExcellenceOrientations(periode) {
  const q = periode ? `?periode=${encodeURIComponent(periode)}` : '';
  const response = await apiRequest(`/admin/excellence/orientations${q}`, { method: 'GET', withAuth: true });
  return response.data;
}

export async function saveExcellenceOpportunite(payload, id) {
  const response = await apiRequest(
    id ? `/admin/excellence/opportunites/${id}` : '/admin/excellence/opportunites',
    {
      method: id ? 'PATCH' : 'POST',
      withAuth: true,
      body: JSON.stringify(payload),
    },
  );
  return response;
}

export async function proposeExcellenceOrientation(payload) {
  const response = await apiRequest('/admin/excellence/orientations', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response;
}

export async function bulkProposeExcellenceShortlist(payload) {
  const response = await apiRequest('/admin/excellence/orientations/bulk-shortlist', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response;
}

export async function updateExcellenceOrientationStatut(id, payload) {
  const response = await apiRequest(`/admin/excellence/orientations/${id}/statut`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response;
}

export async function seedExcellencePartners() {
  const response = await apiRequest('/admin/excellence/seed', { method: 'POST', withAuth: true });
  return response;
}

// ─── Logistique & patrimoine (module 9) ─────────────────────────────────────

export async function fetchLogistiqueDashboard() {
  const response = await apiRequest('/admin/logistique/dashboard', { method: 'GET', withAuth: true });
  return response.data;
}

export async function fetchPatrimoineArticles(params = {}) {
  const qs = new URLSearchParams();
  if (params.categorie) qs.set('categorie', params.categorie);
  if (params.statut) qs.set('statut', params.statut);
  if (params.estConsommable != null) qs.set('estConsommable', String(params.estConsommable));
  if (params.idSalle) qs.set('idSalle', params.idSalle);
  if (params.q) qs.set('q', params.q);
  const q = qs.toString() ? `?${qs}` : '';
  const response = await apiRequest(`/admin/logistique/patrimoine${q}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createPatrimoineArticle(payload) {
  const response = await apiRequest('/admin/logistique/patrimoine', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchPatrimoineArticle(id, payload) {
  const response = await apiRequest(`/admin/logistique/patrimoine/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function adjustPatrimoineStock(id, delta, commentaire) {
  const response = await apiRequest(`/admin/logistique/patrimoine/${encodeURIComponent(id)}/stock`, {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ delta, commentaire }),
  });
  return response.data;
}

export async function deletePatrimoineArticle(id) {
  const response = await apiRequest(`/admin/logistique/patrimoine/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    withAuth: true,
  });
  return response;
}

export async function fetchMouvementsLogistique(idArticle) {
  const q = idArticle ? `?idArticle=${encodeURIComponent(idArticle)}` : '';
  const response = await apiRequest(`/admin/logistique/mouvements${q}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchAffectationsPatrimoine(activeOnly = false) {
  const q = activeOnly ? '?activeOnly=true' : '';
  const response = await apiRequest(`/admin/logistique/affectations${q}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAffectationPatrimoine(payload) {
  const response = await apiRequest('/admin/logistique/affectations', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function closeAffectationPatrimoine(id) {
  const response = await apiRequest(`/admin/logistique/affectations/${encodeURIComponent(id)}/cloturer`, {
    method: 'POST',
    withAuth: true,
  });
  return response.data;
}

export async function fetchInscriptionsEnAttentePoste() {
  const response = await apiRequest('/admin/logistique/postes/en-attente', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchOrdinateursDisponiblesPoste() {
  const response = await apiRequest('/admin/logistique/postes/ordinateurs-disponibles', {
    method: 'GET',
    withAuth: true,
  });
  return response.data ?? [];
}

export async function fetchPostesEtudiantsActifs() {
  const response = await apiRequest('/admin/logistique/postes/actifs', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchPresentsSansPoste(idSession) {
  const q = idSession ? `?idSession=${encodeURIComponent(idSession)}` : '';
  const response = await apiRequest(`/admin/logistique/postes/presents-sans-poste${q}`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data ?? [];
}

export async function affecterPosteApprenant(payload) {
  const response = await apiRequest('/admin/logistique/postes/affecter', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function libererPosteEtudiant(idAffectation) {
  const response = await apiRequest(`/admin/logistique/postes/${encodeURIComponent(idAffectation)}/liberer`, {
    method: 'POST',
    withAuth: true,
  });
  return response.data;
}

export async function fetchMaintenancesPatrimoine() {
  const response = await apiRequest('/admin/logistique/maintenances', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createMaintenancePatrimoine(payload) {
  const response = await apiRequest('/admin/logistique/maintenances', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchMaintenancePatrimoine(id, payload) {
  const response = await apiRequest(`/admin/logistique/maintenances/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchPannesPatrimoine() {
  const response = await apiRequest('/admin/logistique/pannes', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createPannePatrimoine(payload) {
  const response = await apiRequest('/admin/logistique/pannes', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchPannePatrimoine(id, payload) {
  const response = await apiRequest(`/admin/logistique/pannes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchSallesLogistique() {
  const response = await apiRequest('/admin/logistique/salles', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function setSalleEquipements(idSalle, items) {
  const response = await apiRequest(`/admin/logistique/salles/${encodeURIComponent(idSalle)}/equipements`, {
    method: 'PUT',
    withAuth: true,
    body: JSON.stringify({ items }),
  });
  return response.data;
}

export async function fetchReservationsSalle(params = {}) {
  const qs = new URLSearchParams();
  if (params.idSalle) qs.set('idSalle', params.idSalle);
  if (params.depuis) qs.set('depuis', params.depuis);
  if (params.jusquA) qs.set('jusquA', params.jusquA);
  const q = qs.toString() ? `?${qs}` : '';
  const response = await apiRequest(`/admin/logistique/reservations${q}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createReservationSalle(payload) {
  const response = await apiRequest('/admin/logistique/reservations', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchReservationSalle(id, payload) {
  const response = await apiRequest(`/admin/logistique/reservations/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function fetchAdminElearningDashboard() {
  const response = await apiRequest('/admin/elearning/dashboard', { method: 'GET', withAuth: true });
  return response.data;
}

export async function fetchAdminElearningCours(idFiliere) {
  const q = idFiliere ? `?idFiliere=${encodeURIComponent(idFiliere)}` : '';
  const response = await apiRequest(`/admin/elearning/cours${q}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminElearningCours(payload) {
  const response = await apiRequest('/admin/elearning/cours', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminElearningCours(id, payload) {
  const response = await apiRequest(`/admin/elearning/cours/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function deleteAdminElearningCours(id) {
  const response = await apiRequest(`/admin/elearning/cours/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    withAuth: true,
  });
  return response.data;
}

export async function fetchAdminElearningRessources(params = {}) {
  const qs = new URLSearchParams();
  if (params.idFiliere) qs.set('idFiliere', params.idFiliere);
  if (params.idCours) qs.set('idCours', params.idCours);
  if (params.type) qs.set('type', params.type);
  if (params.q) qs.set('q', params.q);
  if (params.categorie) qs.set('categorie', params.categorie);
  if (params.tag) qs.set('tag', params.tag);
  const q = qs.toString() ? `?${qs}` : '';
  const response = await apiRequest(`/admin/elearning/ressources${q}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminElearningRessource(payload) {
  const response = await apiRequest('/admin/elearning/ressources', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminElearningRessource(id, payload) {
  const response = await apiRequest(`/admin/elearning/ressources/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function deleteAdminElearningRessource(id) {
  const response = await apiRequest(`/admin/elearning/ressources/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    withAuth: true,
  });
  return response.data;
}

export async function fetchAdminElearningForums(idFiliere) {
  const q = idFiliere ? `?idFiliere=${encodeURIComponent(idFiliere)}` : '';
  const response = await apiRequest(`/admin/elearning/forums${q}`, { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminElearningForum(payload) {
  const response = await apiRequest('/admin/elearning/forums', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminElearningForum(id, payload) {
  const response = await apiRequest(`/admin/elearning/forums/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function deleteAdminElearningForum(id) {
  const response = await apiRequest(`/admin/elearning/forums/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    withAuth: true,
  });
  return response.data;
}

export async function fetchAdminElearningCompletionStats(idFiliere) {
  const q = idFiliere ? `?idFiliere=${encodeURIComponent(idFiliere)}` : '';
  const response = await apiRequest(`/admin/elearning/stats/completion${q}`, { method: 'GET', withAuth: true });
  return response.data;
}

export async function fetchAdminElearningForumMessages(idForum) {
  const response = await apiRequest(`/admin/elearning/forums/${encodeURIComponent(idForum)}/messages`, {
    method: 'GET',
    withAuth: true,
  });
  return response.data ?? [];
}

export async function moderateAdminElearningForumMessage(messageId, payload) {
  const response = await apiRequest(`/admin/elearning/forums/messages/${encodeURIComponent(messageId)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function uploadAdminElearningFile(file) {
  const form = new FormData();
  form.append('file', file);
  const response = await apiRequest('/admin/elearning/upload', { method: 'POST', withAuth: true, body: form });
  return response.data;
}

export async function fetchHistoriqueLogistique() {
  const response = await apiRequest('/admin/logistique/historique', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchAdminEquipeModules() {
  const response = await apiRequest('/admin/equipe/modules', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function fetchAdminModerateurs() {
  const response = await apiRequest('/admin/equipe/moderateurs', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminModerateur(payload) {
  const response = await apiRequest('/admin/equipe/moderateurs', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response;
}

export async function updateModerateurModules(id, modules) {
  const response = await apiRequest(`/admin/equipe/moderateurs/${encodeURIComponent(id)}/modules`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ modules }),
  });
  return response.data;
}

export async function updateModerateurStatut(id, estCompteActif) {
  const response = await apiRequest(`/admin/equipe/moderateurs/${encodeURIComponent(id)}/statut`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify({ estCompteActif }),
  });
  return response.data;
}

export async function resendModerateurAccess(id) {
  const response = await apiRequest(`/admin/equipe/moderateurs/${encodeURIComponent(id)}/renvoyer-acces`, {
    method: 'POST',
    withAuth: true,
  });
  return response;
}

async function deleteAdminEntity(path) {
  const response = await apiRequest(path, { method: 'DELETE', withAuth: true });
  if (response?.success === false) {
    throw new Error(response?.message || 'Suppression impossible');
  }
  return response;
}

export async function deleteAdminInscription(id) {
  return deleteAdminEntity(`/admin/inscriptions/${encodeURIComponent(id)}`);
}

export async function deleteAdminApprenant(id) {
  return deleteAdminEntity(`/admin/apprenants/${encodeURIComponent(id)}`);
}

export async function deleteAdminFormateur(id) {
  return deleteAdminEntity(`/admin/formateurs/${encodeURIComponent(id)}`);
}

export async function deleteAdminAmbassadeur(id) {
  return deleteAdminEntity(`/admin/ambassadeurs/${encodeURIComponent(id)}`);
}

export async function deleteAdminCohorte(id) {
  return deleteAdminEntity(`/admin/cohortes/${encodeURIComponent(id)}`);
}

export async function deleteAdminFiliere(id) {
  return deleteAdminEntity(`/admin/filieres/${encodeURIComponent(id)}`);
}

export async function deleteAdminDomaine(id) {
  return deleteAdminEntity(`/admin/domaines/${encodeURIComponent(id)}`);
}

export async function deleteAdminSalle(id) {
  return deleteAdminEntity(`/admin/salles/${encodeURIComponent(id)}`);
}

export async function deleteAdminOpportunite(id) {
  return deleteAdminEntity(`/admin/excellence/opportunites/${encodeURIComponent(id)}`);
}

export async function deleteAdminOrientation(id) {
  return deleteAdminEntity(`/admin/excellence/orientations/${encodeURIComponent(id)}`);
}

export async function deleteAdminNotification(id) {
  return deleteAdminEntity(`/admin/communication/notifications/${encodeURIComponent(id)}`);
}

const USER_GUIDE_PDF_FILENAME = 'documentation-utilisateur-jconsult-academia.pdf';

export async function downloadUserGuidePdf() {
  const { blobUrl } = await fetchAuthenticatedFileBlob('/admin/documentation-utilisateur/pdf');
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = USER_GUIDE_PDF_FILENAME;
  anchor.click();
  URL.revokeObjectURL(blobUrl);
}

export async function fetchAdminPartenairesSponsors() {
  const response = await apiRequest('/admin/partenaires-sponsors', { method: 'GET', withAuth: true });
  return response.data ?? [];
}

export async function createAdminPartenaireSponsor(payload) {
  const response = await apiRequest('/admin/partenaires-sponsors', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function patchAdminPartenaireSponsor(id, payload) {
  const response = await apiRequest(`/admin/partenaires-sponsors/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function deleteAdminPartenaireSponsor(id) {
  await apiRequest(`/admin/partenaires-sponsors/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    withAuth: true,
  });
}
